import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VendorProductsService } from '../../../services/vendorproducts/vendorproducts.service';
import { RewardsAdminService } from '../../../services/rewards-admin/rewards-admin.service';
import { CompanyProductSettingMode } from '../companyproductsdialog/companyproductsdialog.component';

export interface CompanyProductTilesCategoriesDialogResult {
  mode: CompanyProductSettingMode;
  productTilesCategoriesMap: Record<string, string[]>;
}

@Component({
    selector: 'app-company-product-tiles-categories-dialog',
    templateUrl: './company-product-tiles-categories-dialog.component.html',
    styleUrls: ['./company-product-tiles-categories-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanyProductTilesCategoriesDialogComponent implements OnInit {
  public items: any[] = [];
  public mode: CompanyProductSettingMode;
  public draftCategoryByProductId: Record<string, string> = {};
  public rewardDefaultCategoriesMap: Record<string, string[]> = {};
  private effectiveCategoriesMap: Record<string, string[]> = {};
  private customCategoriesMap: Record<string, string[]> = {};

  constructor(
    private vendorProductsService: VendorProductsService,
    private rewardsAdminService: RewardsAdminService,
    public dialogRef: MatDialogRef<CompanyProductTilesCategoriesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CompanyProductTilesCategoriesDialogModel
  ) {
    this.mode = data?.initialMode || 'custom';
    this.effectiveCategoriesMap = this.normalizeMap(data?.productTilesCategoriesMap || {});
    this.customCategoriesMap = this.mode === 'none'
      ? {}
      : this.cloneMap(this.effectiveCategoriesMap);
  }

  ngOnInit(): void {
    const selectedProductIds: string[] = Array.isArray(this.data?.products) ? this.data.products : [];
    this.vendorProductsService.getAllVendorProducts().then(async (products) => {
      const productById = new Map((products || []).map((product: any) => [product.id, product]));
      this.items = selectedProductIds
        .map((productId: string) => productById.get(productId))
        .filter((product: any) => !!product);

      // Keep only keys that are currently selected products.
      const selectedEffectiveMap: Record<string, string[]> = {};
      const selectedCustomMap: Record<string, string[]> = {};
      selectedProductIds.forEach((productId: string) => {
        selectedEffectiveMap[productId] = this.effectiveCategoriesMap[productId] || [];
        selectedCustomMap[productId] = this.customCategoriesMap[productId] || [];
        this.draftCategoryByProductId[productId] = '';
      });
      this.effectiveCategoriesMap = selectedEffectiveMap;
      this.customCategoriesMap = selectedCustomMap;
      this.rewardDefaultCategoriesMap = await this.loadRewardDefaultCategories(this.items);
    }).catch((error) => {
      console.error('Failed to load product tile categories dialog data', error);
      this.items = [];
      this.rewardDefaultCategoriesMap = {};
    });
  }

  canInherit(): boolean {
    return !!this.data.canInherit;
  }

  setMode(mode: CompanyProductSettingMode): void {
    this.mode = mode;
  }

  isCustomMode(): boolean {
    return this.mode === 'custom';
  }

  getCategories(productId: string): string[] {
    if (this.mode === 'inherit') {
      return this.effectiveCategoriesMap[productId] || [];
    }

    if (this.mode === 'none') {
      return [];
    }

    return this.customCategoriesMap[productId] || [];
  }

  getRewardDefaultCategories(productId: string): string[] {
    return this.rewardDefaultCategoriesMap[productId] || [];
  }

  onDraftCategoryChange(productId: string, value: string): void {
    this.draftCategoryByProductId[productId] = value;
  }

  addDraftCategory(productId: string): void {
    if (!this.isCustomMode()) {
      return;
    }

    const value = `${this.draftCategoryByProductId[productId] || ''}`.trim();
    if (!value) {
      return;
    }

    const existing = this.customCategoriesMap[productId] || [];
    if (!existing.includes(value)) {
      this.customCategoriesMap = {
        ...this.customCategoriesMap,
        [productId]: [...existing, value]
      };
    }
    this.draftCategoryByProductId[productId] = '';
  }

  removeCategory(productId: string, category: string): void {
    if (!this.isCustomMode()) {
      return;
    }

    const existing = this.customCategoriesMap[productId] || [];
    const next = existing.filter(item => item !== category);
    this.customCategoriesMap = {
      ...this.customCategoriesMap,
      [productId]: next
    };
  }

  okClicked(): void {
    if (this.mode !== 'custom') {
      this.dialogRef.close({
        mode: this.mode,
        productTilesCategoriesMap: {}
      });
      return;
    }

    const cleanedMap: Record<string, string[]> = {};
    let hasExplicitCompanyOverride = false;
    Object.entries(this.customCategoriesMap).forEach(([productId, categories]) => {
      const cleanedCategories = this.cleanCategories(categories);
      if (cleanedCategories.length > 0) {
        cleanedMap[productId] = cleanedCategories;
        hasExplicitCompanyOverride = true;
      }
    });

    if (!hasExplicitCompanyOverride) {
      const defaultFallbackMap = this.items.reduce((result, item: any) => {
        const productId = `${item?.id || ''}`.trim();
        if (productId) {
          result[productId] = [];
        }
        return result;
      }, {} as Record<string, string[]>);

      this.dialogRef.close({
        mode: this.mode,
        productTilesCategoriesMap: defaultFallbackMap
      });
      return;
    }

    this.dialogRef.close({
      mode: this.mode,
      productTilesCategoriesMap: cleanedMap
    });
  }

  onDismiss(): void {
    this.dialogRef.close(undefined);
  }

  private normalizeMap(map: Record<string, any>): Record<string, string[]> {
    const normalized: Record<string, string[]> = {};
    Object.entries(map || {}).forEach(([productId, categories]) => {
      normalized[productId] = this.cleanCategories(Array.isArray(categories) ? categories : []);
    });
    return normalized;
  }

  private cloneMap(map: Record<string, string[]>): Record<string, string[]> {
    return JSON.parse(JSON.stringify(map || {}));
  }

  private async loadRewardDefaultCategories(items: any[]): Promise<Record<string, string[]>> {
    const rewardIdsByProductId: Record<string, string[]> = {};
    const uniqueRewardIds = new Set<string>();
    (items || []).forEach((item: any) => {
      const productId = `${item?.id || ''}`.trim();
      if (!productId) {
        return;
      }

      const rewardIds = this.extractRewardIds(item);
      rewardIdsByProductId[productId] = rewardIds;
      rewardIds.forEach((rewardId) => uniqueRewardIds.add(rewardId));
    });

    const rewardById = new Map<string, any>();
    await Promise.all(Array.from(uniqueRewardIds).map(async (rewardId) => {
      try {
        rewardById.set(rewardId, await this.rewardsAdminService.getRewardById(rewardId));
      } catch (error) {
        console.error(`Failed to load reward ${rewardId} for product tile category defaults`, error);
      }
    }));

    return Object.entries(rewardIdsByProductId).reduce((result, [productId, rewardIds]) => {
      result[productId] = this.cleanCategories((rewardIds || []).reduce((categories: string[], rewardId: string) => {
        return categories.concat(this.extractBandCategories(rewardById.get(rewardId)));
      }, []));
      return result;
    }, {} as Record<string, string[]>);
  }

  private extractRewardIds(item: any): string[] {
    const metadata = this.toObject(item?.metadata);
    const rewardMetadata = this.toObject(metadata?.reward);
    const rewardIds = Array.isArray(rewardMetadata?.id) ? rewardMetadata.id : [rewardMetadata?.id];
    return this.cleanCategories(rewardIds);
  }

  private extractBandCategories(reward: any): string[] {
    const rewardDetails = this.toObject(reward?.rewardDetails);
    const cloDetails = this.toObject(rewardDetails?.clo);
    return this.cleanCategories(Array.isArray(cloDetails?.bandCategories) ? cloDetails.bandCategories : []);
  }

  private toObject(value: any): any {
    if (!value) {
      return {};
    }

    if (typeof value === 'object') {
      return value;
    }

    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }

    return {};
  }

  private cleanCategories(categories: any[]): string[] {
    const clean: string[] = [];
    (categories || []).forEach((category: any) => {
      const normalized = `${category ?? ''}`.trim();
      if (normalized && !clean.includes(normalized)) {
        clean.push(normalized);
      }
    });
    return clean;
  }
}

export class CompanyProductTilesCategoriesDialogModel {
  constructor(
    public products: string[] = [],
    public productTilesCategoriesMap: Record<string, string[]> = {},
    public companyName: string = '',
    public canInherit: boolean = false,
    public parentCompanyName: string = '',
    public initialMode: CompanyProductSettingMode = 'custom',
  ) {}
}
